import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, Autocomplete, useMediaQuery } from '@mui/material';
import Dropdown from '../ui/dropdown';
import timezones from '../../files/json/timezones.json';
import currencies from '../../files/json/currencies.json';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

type AddCountryModalProps = {
	open: boolean;
	onClose: () => void;
	onSubmit: (data: any) => void;
	isEdit?: boolean;
	editData?: {
		id: number;
		country: string;
		timezone: string;
		currency: string;
		dateFormat: string;
		fiscalYearStartMonth?: number | null;
		status: boolean;
	};
};

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    .map((month, i) => ({ value: String(i + 1), label: month }));

const dateFormats = [
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
];

const AddCountryModal: React.FC<AddCountryModalProps> = ({ open, onClose, onSubmit, isEdit = false, editData }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [country, setCountry] = useState('');
    const [timezone, setTimezone] = useState<{ label: string; value: string } | null>(null);
    const [currency, setCurrency] = useState<{ label: string; value: string } | null>(null);
    const [dateFormat, setDateFormat] = useState('');
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState('');
	const [errors, setErrors] = useState<{ country?: string; timezone?: string; currency?: string; dateFormat?: string; fiscalYearStartMonth?: string }>({});

	// Prepare options for Autocomplete
	const timezoneOptions = timezones.map(tz => ({ label: tz.text, value: tz.value }));
	const currencyOptions = currencies.map(cur => ({ label: `${cur.name} (${cur.symbol})`, value: cur.name }));

	useEffect(() => {
		setCountry('');
		setTimezone(null);
		setCurrency(null);
		setDateFormat('');
		setFiscalYearStartMonth('');
		setErrors({});

		if (isEdit && editData) {
			setCountry(editData.country);
			setTimezone(timezoneOptions.find(tz => tz.value === editData.timezone) || null);
			setCurrency(currencyOptions.find(cur => cur.value === editData.currency) || null);
			setDateFormat(editData.dateFormat);
			setFiscalYearStartMonth(editData.fiscalYearStartMonth ? String(editData.fiscalYearStartMonth) : '');
		}
	}, [isEdit, editData, open]);

	const handleSubmit = () => {
		const newErrors: { country?: string; timezone?: string; currency?: string; dateFormat?: string; fiscalYearStartMonth?: string } = {};
		let isValid = true;
		if (!country) {
			newErrors.country = 'Country name is required';
			isValid = false;
		}
		if (!timezone) {
			newErrors.timezone = 'Timezone is required';
			isValid = false;
		}
		if (!currency) {
			newErrors.currency = 'Currency is required';
			isValid = false;
		}
		if (!dateFormat) {
			newErrors.dateFormat = 'Date format is required';
			isValid = false;
		}
		if (!fiscalYearStartMonth) {
			newErrors.fiscalYearStartMonth = 'Fiscal year start month is required';
			isValid = false;
		}
		if (isValid) {
			onSubmit({ country, timezone: timezone?.value, currency: currency?.value, dateFormat, fiscalYearStartMonth: Number(fiscalYearStartMonth) });
		} else {
			setErrors(newErrors);
		}
	};

	const handleClose = () => {
		setCountry('');
		setTimezone(null);
		setCurrency(null);
		setDateFormat(dateFormats[0].value);
		setFiscalYearStartMonth('');
		setErrors({});
		onClose();
	};

	const content = (
		<>
			<DialogTitle variant="h6" fontWeight={600} >{isEdit ? 'Edit Country' : 'Add New Country'}</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={3} mt={1}>
                    <Box>
						<Typography mb={1} fontSize={14} color='var(--color-text-muted)'>Country Name <span style={{ color: 'red' }}>*</span></Typography>
						<TextField
                            value={country}
                            onChange={e => setCountry(e.target.value)}
                            size="small"
                            fullWidth
							placeholder='Enter country name'
                            error={!!errors.country}
                            helperText={errors.country}
                        />
					</Box>
                    <Box>
						<Typography mb={1} fontSize={14} color='var(--color-text-muted)'>Timezone <span style={{ color: 'red' }}>*</span></Typography>
						<Autocomplete
							options={timezoneOptions}
							value={timezone}
							onChange={(_, v) => setTimezone(v)}
							getOptionLabel={opt => opt ? opt.label : ''}
							renderInput={params => (
								<TextField
									{...params}
									size="small"
									error={!!errors.timezone}
									helperText={errors.timezone}
									placeholder='Select timezone'
								/>
							)}
							isOptionEqualToValue={(opt, val) => opt.value === val?.value}
							fullWidth
						/>
					</Box>
					<Box>
						<Typography mb={1} fontSize={14} color='var(--color-text-muted)'>Currency <span style={{ color: 'red' }}>*</span></Typography>
						<Autocomplete
							options={currencyOptions}
							value={currency}
							onChange={(_, v) => setCurrency(v)}
							getOptionLabel={opt => opt ? opt.label : ''}
							renderInput={params => (
								<TextField
									{...params}
									size="small"
									error={!!errors.currency}
									helperText={errors.currency}
									placeholder='Select currency'
								/>
							)}
							isOptionEqualToValue={(opt, val) => opt.value === val?.value}
							fullWidth
						/>
					</Box>
					<Box>
						<Typography mb={1} fontSize={14} color='var(--color-text-muted)'>Date Format <span style={{ color: 'red' }}>*</span></Typography>
						<Dropdown
							options={dateFormats}
							value={dateFormat}
							onChange={setDateFormat}
                            height={40}
							error={!!errors.dateFormat}
							helperText={errors.dateFormat}
							placeholder='Select date format'
						/>
					</Box>
					<Box>
						<Typography mb={1} fontSize={14} color='var(--color-text-muted)'>Fiscal Year Start Month <span style={{ color: 'red' }}>*</span></Typography>
						<Dropdown
							options={months}
							value={fiscalYearStartMonth}
							onChange={setFiscalYearStartMonth}
                            height={40}
							error={!!errors.fiscalYearStartMonth}
							helperText={errors.fiscalYearStartMonth}
							placeholder='Select month'
						/>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
				<Button
					onClick={handleClose}
					variant="contained"
					style={{
						background: 'var(--color-container-background)',
						color: 'var(--color-default-text)',
						borderRadius: 8,
						fontWeight: 600,
						boxShadow: 'none',
						textTransform: 'none'
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					style={{
						background: 'var(--color-primary)',
						color: '#fff',
						borderRadius: 8,
						fontWeight: 600,
						boxShadow: 'none',
						textTransform: 'none'
					}}
				>
					{isEdit ? 'Save Changes' : 'Add Country'}
				</Button>
			</DialogActions>
		</>
	);

	return isMobile ? (
		<BottomSheetDialog open={open} onClose={handleClose} heightVh={90}>
			{content}
		</BottomSheetDialog>
	) : (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}>
			{content}
		</Dialog>
	);
};

export default AddCountryModal;
